import 'core-js/stable';
import 'regenerator-runtime/runtime';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { unregisterServiceWorker } from './registerServiceWorker';
import configureStore from './store/configureStore';

// Load font stylesheet dynamically from env variable.
// VITE_FontStylesheetUrl, when set, is used as-is (supports Adobe Fonts / Typekit etc.).
// Otherwise falls back to Google Fonts built from VITE_FontFamilyDownload.
const fontStylesheetUrl = import.meta.env.VITE_FontStylesheetUrl;
const fontFamily = import.meta.env.VITE_FontFamilyDownload || 'Poppins';
const fontUrl =
    fontStylesheetUrl && fontStylesheetUrl !== '__FONTSTYLESHEETURL__'
        ? fontStylesheetUrl
        : fontFamily && fontFamily !== '__FONTFAMILY__'
          ? `https://fonts.googleapis.com/css?family=${encodeURIComponent(fontFamily)}&display=swap&subset=latin-ext`
          : null;

if (fontUrl) {
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.href = fontUrl;
    preloadLink.as = 'style';
    preloadLink.crossOrigin = 'anonymous';
    document.head.appendChild(preloadLink);

    const styleLink = document.createElement('link');
    styleLink.rel = 'stylesheet';
    styleLink.href = fontUrl;
    document.head.appendChild(styleLink);
}

import { ContextProvider, contextSaga, ReferenceProvider, referenceSaga } from 'metrix-common3';
import AppWithConstants from '@/provider/constants';

import StoreFrontProvider from '@/provider/storefront';
import storefrontSaga from '@/sagas/storefront/saga';
import App from './containers/App';

import { enMessages, frMessages } from './messages';

function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
}

// Get language preference with a fallback chain
const preferredCulture = getCookie('preferredCulture') || localStorage.getItem('preferredCulture') || 'en-CA';

// Override fetch to use the preferred culture
const originalFetch = window.fetch;
window.fetch = (url, options = {}) => {
    if (!options.headers) {
        options.headers = {};
    }

    options.headers = {
        'Accept-Language': preferredCulture,
        ...options.headers,
    };

    return originalFetch(url, options);
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error) {
        this.setState({ hasError: true, error });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <h2>Something went wrong.</h2>
                    <p>Please refresh the page to try again.</p>
                </div>
            );
        }

        return this.props.children;
    }
}

// Create Redux store with initial state
const initialState = window.initialReduxState || {};
const store = configureStore(initialState);

// Run sagas
store.runSaga(contextSaga);
store.runSaga(referenceSaga);
store.runSaga(storefrontSaga);

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <ErrorBoundary>
            <Provider store={store}>
                <BrowserRouter basename={import.meta.env.BASE_URL}>
                    <HelmetProvider>
                        <AppWithConstants>
                            <ContextProvider messages={{ en: enMessages, fr: frMessages }}>
                                <StoreFrontProvider>
                                    <ReferenceProvider>
                                        <App />
                                    </ReferenceProvider>
                                </StoreFrontProvider>
                            </ContextProvider>
                        </AppWithConstants>
                    </HelmetProvider>
                </BrowserRouter>
            </Provider>
        </ErrorBoundary>
    </React.StrictMode>,
);

unregisterServiceWorker();

// Hot Module Replacement (HMR) - Vite style
if (import.meta.hot) {
    import.meta.hot.accept();
}
